import React, { memo, useMemo } from "react";
import moment from "moment";
import { HiClipboardList, HiExclamation, HiUserGroup, HiUsers, HiAdjustments } from "react-icons/hi";
import SelectOption from "components/common/Select/Select";
import { DUMMY_REGEX_EXP } from "Constants/constant";
import {
  capitalizeWords,
  parseBooleanVal,
  validateCurrentDate,
} from "Utils/utils";
import { groupServiceTypesByFundingType } from "Utils/rosteringFundingType";
import Loader from "components/common/Loader/Loader";
import useCareLabels from "hooks/useCareLabels";
import WorkerSelection from "../Selection/WorkerSelection";
import ParticipantSelection from "../Selection/ParticiapantSelection";
import SplitShift from "./SplitShift";
import ShiftTasksNotesSection from "./ShiftTasksNotesSection";
import BasicModeShiftWhen from "./BasicModeShiftWhen";
import ShiftDatePicker from "./ShiftDatePicker";
import { getBasicShiftDurationHHMM } from "./basicModeValidation";
import { toTimeInputValue } from "./advanceModeSplitDefaults";
import {
  ADVANCE_TIME_INPUT_CLASS,
  AdvanceModeFieldLabel,
  AdvanceModeInfoCallout,
  AdvanceModeSection,
  AdvanceModeSectionBody,
  AdvanceModeSectionHeader,
  AdvanceModeShiftOverlapAlert,
  AdvanceModeTimeRange,
  AdvanceModeWorkerAvailabilityAlert,
} from "./advanceModeUi";
import { isAdvanceShiftDraft } from "./advanceModeDraftUtils";
import "./advanceModeTimePicker.css";

const BASIC_SECTION_IDS = {
  schedule: "basic-mode-section-schedule",
  participants: "basic-mode-section-participants",
  workers: "basic-mode-section-workers",
  service: "basic-mode-section-service",
  tasksNotes: "basic-mode-section-tasks-notes",
};

const ratioLabel = (
  calendarView,
  selectedShift,
  shiftDetailData,
  workerRatio,
  participantRatio
) => {
  const workers = Array.isArray(workerRatio)
    ? workerRatio?.length
    : workerRatio
    ? 1
    : 0;
  const participants =
    calendarView === "participant-view" &&
    selectedShift &&
    shiftDetailData?.participants?.length > 0
      ? shiftDetailData?.participants?.length
      : Array.isArray(participantRatio)
      ? participantRatio?.length
      : participantRatio
      ? 1
      : 0;
  return { workers, participants };
};

const BasicModeView = ({
  onEnterAdvanceMode,
  shiftOverlap,
  allowShiftOverlapped,
  onCheckWorkerAvailability,
  getWorkerProfileLoader,
  workerAvailabilityLoadingId,
  onLeave,
  onLeaveMessage,
  isLessThan12Hours,
  selectedWorker,
  watchFormData,
  calendarView,
  selectedShift,
  sortedWorkerOptions,
  register,
  setValue,
  getValues,
  trigger,
  control,
  watch,
  getSelectedOption,
  setSelectedOptions,
  selectedOptions,
  clearErrors,
  errors,
  isCallFromAddShift,
  setIsWorkerLoaded,
  isWorkerLoaded,
  setWorkerOptionsSorted,
  setParticipantOptionsSorted,
  checkDisabilityOfShiftStatus,
  isShiftDateLocked = false,
  onShiftDateChange = null,
  checkDisabliltyOfShiftForWorker,
  checkRunningShiftStatus,
  handleChangeWorkerOrParticipant,
  userItem,
  isFetchRecordDetailLoader,
  setIsParticipantLoaded,
  getSelectedParticipant,
  createOrUpdateWorkerShiftLoader,
  handleChangeServiceCategory,
  participantOptions,
  handleAddSplitShift,
  disablDueToRecurringShift,
  shiftDetailData,
  setShouldCallHandleChangeWorkerOrParticipant,
  serviceTypeSelectedOptions,
  isParticipantLoaded,
  selectedParticipant,
  isShiftSplit,
  serviceTypes,
  selectedServiceCategory,
  showServiceCategoryError = false,
  spRosteringSetting,
  workerRatio,
  participantRatio,
  splitShifts,
  handleRemoveSplitShift,
  setIsLessThan12Hours,
  tasks,
  notes,
  handleTaskChange,
  handleTaskMandatoryChange,
  handleAddTask,
  handleTaskDelete,
  handleNoteChange,
  isShiftInstructionLoading,
  shiftInstructions,
  editableInstructionIndex,
  handleShiftInstructionChange,
  handleRemoveShiftInstruction,
  handleAddShiftInstruction,
  prefillFromShiftRequest = null,
}) => {
  const { participantLabel, participantLabelPlural } = useCareLabels();
  const { workers: workerCount, participants: participantCount } = ratioLabel(
    calendarView,
    selectedShift,
    shiftDetailData,
    workerRatio,
    participantRatio
  );

  const totalShiftDuration = useMemo(
    () =>
      getBasicShiftDurationHHMM(
        watchFormData?.start_time,
        watchFormData?.end_time
      ),
    [watchFormData?.start_time, watchFormData?.end_time]
  );

  // Same funding-type grouping as staging / SplitShift (NDIS SERVICE CATEGORIES header).
  const groupedServiceTypes = useMemo(() => {
    const types = [...(serviceTypes || [])];
    if (
      selectedServiceCategory &&
      !types.some(
        (item) => String(item?.value) === String(selectedServiceCategory?.value)
      )
    ) {
      types.push(selectedServiceCategory);
    }
    return groupServiceTypesByFundingType(types);
  }, [serviceTypes, selectedServiceCategory]);

  const splitSegments = isShiftSplit ? (
    <div className='mt-2.5 space-y-2 border-t border-slate-100/90 pt-2.5'>
      <div className='flex flex-wrap items-center justify-between gap-1.5'>
        <p className='text-[11px] font-semibold uppercase tracking-wide text-slate-500'>
          Split segments
        </p>
        <p className='text-xs font-medium text-slate-700'>
          Ratio (Worker: {participantLabelPlural}){" "}
          <span className='font-semibold tabular-nums text-slate-900'>
            {workerCount}
          </span>
          {" : "}
          <span className='font-semibold tabular-nums text-slate-900'>
            {participantCount}
          </span>
        </p>
      </div>
      <div className='flex flex-col gap-1.5'>
        {splitShifts.map((field, index) => (
          <SplitShift
            key={field.id || index}
            index={index}
            shift={field}
            watch={watch}
            register={register}
            setValue={setValue}
            control={control}
            errors={errors}
            clearErrors={clearErrors}
            serviceTypes={serviceTypes}
            selectedParticipant={selectedParticipant}
            onRemove={() => handleRemoveSplitShift(index)}
            totalShiftDuration={totalShiftDuration}
            setIsLessThan12Hours={setIsLessThan12Hours}
            spRosteringSetting={spRosteringSetting}
            useNativeTimeInput
            showServiceCategoryError={showServiceCategoryError}
          />
        ))}
      </div>
    </div>
  ) : null;

  const workerShiftDate =
    watchFormData?.shift_date ?? watchFormData?.date ?? null;
  const formattedWorkerShiftDate = workerShiftDate
    ? moment(workerShiftDate, ["YYYY-MM-DD", "DD-MM-YYYY"], true).isValid()
      ? moment(workerShiftDate, ["YYYY-MM-DD", "DD-MM-YYYY"], true).format(
          "YYYY-MM-DD"
        )
      : moment(workerShiftDate).format("YYYY-MM-DD")
    : "";

  const workerTimesPanel =
    shiftDetailData && shiftDetailData?.worker_start_time ? (
      <>
        {/* Keep RHF values; display uses schedule-aligned controls below */}
        <input type='hidden' {...register("worker_start_time")} />
        <input type='hidden' {...register("worker_end_time")} />
        <div className='mt-2.5 grid min-w-0 grid-cols-1 gap-2 border-t border-slate-100/90 pt-2.5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] lg:gap-2.5'>
          <div className='advance-mode-panel min-w-0'>
            <AdvanceModeFieldLabel>Shift date</AdvanceModeFieldLabel>
            <span
              className='mb-1 block text-[10px] font-medium invisible'
              aria-hidden
            >
              Start
            </span>
            <ShiftDatePicker
              setValue={setValue}
              register={register}
              pattern={
                validateCurrentDate(
                  moment(workerShiftDate),
                  moment(new Date()),
                  ["present", "future", "past"],
                  true
                )
                  ? DUMMY_REGEX_EXP
                  : null
              }
              value={formattedWorkerShiftDate}
              name='shift_date'
              isDisabled={selectedShift}
              isRequired={true}
              readOnly={true}
              ErrorMessage={
                errors?.shift_date?.type === "required"
                  ? "This field is required!"
                  : errors?.shift_date?.type === "pattern"
                  ? validateCurrentDate(
                      moment(watchFormData?.shift_date ?? null),
                      moment(new Date()),
                      ["present", "future", "past"],
                      false
                    )
                  : null
              }
            />
          </div>
          <div className='advance-mode-panel min-w-0'>
            <AdvanceModeFieldLabel>Actual times</AdvanceModeFieldLabel>
            <AdvanceModeTimeRange
              startField={
                <>
                  <span className='mb-1 block text-[10px] font-medium text-slate-500'>
                    Start
                  </span>
                  <input
                    type='time'
                    className={ADVANCE_TIME_INPUT_CLASS}
                    readOnly
                    disabled
                    tabIndex={-1}
                    value={toTimeInputValue(watchFormData?.worker_start_time)}
                    aria-label='Worker start time'
                  />
                </>
              }
              endField={
                <>
                  <span className='mb-1 block text-[10px] font-medium text-slate-500'>
                    End
                  </span>
                  <input
                    type='time'
                    className={ADVANCE_TIME_INPUT_CLASS}
                    readOnly
                    disabled
                    tabIndex={-1}
                    value={toTimeInputValue(watchFormData?.worker_end_time)}
                    aria-label='Worker end time'
                  />
                </>
              }
            />
          </div>
        </div>
      </>
    ) : null;

  return (
    <div className='advance-mode-root basic-mode-root min-w-0 max-w-full space-y-2 p-0.5 sm:p-1'>
      <AdvanceModeShiftOverlapAlert
        shiftOverlap={shiftOverlap}
        allowShiftOverlapped={allowShiftOverlapped}
      />
      <AdvanceModeWorkerAvailabilityAlert
        shiftOverlap={shiftOverlap}
        onCheckAvailability={onCheckWorkerAvailability}
        getWorkerProfileLoader={getWorkerProfileLoader}
        loadingItemId={workerAvailabilityLoadingId}
      />

      {onLeave && onLeaveMessage && (
        <AdvanceModeInfoCallout tone='amber' icon={HiExclamation}>
          {onLeaveMessage}
        </AdvanceModeInfoCallout>
      )}

      {isLessThan12Hours && (
        <AdvanceModeInfoCallout tone='amber' icon={HiExclamation}>
          Please note that sleepover shift under SCHADS must be at least 12 hours
        </AdvanceModeInfoCallout>
      )}

      {/* 1. Workers | Participants — original basic order (before schedule) */}
      <div className='advance-mode-participant-worker-grid grid min-w-0 grid-cols-1 items-start gap-2 xl:grid-cols-2'>
        <div id={BASIC_SECTION_IDS.workers} className='scroll-mt-3'>
          <AdvanceModeSection className='flex h-full flex-col'>
            <AdvanceModeSectionHeader
              theme='sky'
              icon={HiUserGroup}
              title='Worker'
              bordered={false}
              compact
            />
            <AdvanceModeSectionBody className='!pt-0' compact>
              <WorkerSelection
                selectedWorker={selectedWorker}
                watchFormData={watchFormData}
                calendarView={calendarView}
                selectedShift={selectedShift}
                workerOptions={sortedWorkerOptions}
                register={register}
                setValue={setValue}
                control={control}
                watch={watch}
                getSelectedOption={getSelectedOption}
                setSelectedOptions={setSelectedOptions}
                selectedOptions={selectedOptions}
                clearErrors={clearErrors}
                errors={errors}
                isCallFromAddShift={isCallFromAddShift}
                setIsWorkerLoaded={setIsWorkerLoaded}
                isWorkerLoaded={isWorkerLoaded}
                setWorkerOptions={setWorkerOptionsSorted}
                setParticipantOptions={setParticipantOptionsSorted}
                checkDisabilityOfShiftStatus={checkDisabilityOfShiftStatus}
                checkDisabliltyOfShiftForWorker={checkDisabliltyOfShiftForWorker}
                checkRunningShiftStatus={checkRunningShiftStatus}
                handleChangeWorkerOrParticipant={handleChangeWorkerOrParticipant}
                userItem={userItem}
                isFetchRecordDetailLoader={isFetchRecordDetailLoader}
                setIsParticipantLoaded={setIsParticipantLoaded}
                isOpenShift={watchFormData?.open_shift}
              />
            </AdvanceModeSectionBody>
          </AdvanceModeSection>
        </div>

        <div id={BASIC_SECTION_IDS.participants} className='scroll-mt-3'>
          <AdvanceModeSection className='flex h-full flex-col'>
            <AdvanceModeSectionHeader
              theme='emerald'
              icon={HiUsers}
              title={participantLabel}
              bordered={false}
              compact
              action={
                selectedShift === null || isAdvanceShiftDraft(selectedShift) ? (
                  <button
                    type='button'
                    onClick={onEnterAdvanceMode}
                    title={`Split ${participantLabelPlural.toLowerCase()}, workers and billing across one shift`}
                    className='group inline-flex items-center justify-center gap-1.5 rounded-lg border border-indigo-200/90 bg-indigo-50/80 px-2.5 py-1.5 text-[11px] font-semibold text-indigo-700 transition hover:border-indigo-300 hover:bg-indigo-50 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/35'
                  >
                    <HiAdjustments
                      className='h-3.5 w-3.5 text-indigo-500 transition group-hover:scale-110'
                      aria-hidden
                    />
                    Advanced edit
                  </button>
                ) : null
              }
            />
            <AdvanceModeSectionBody className='!pt-0' compact>
              <ParticipantSelection
                getSelectedOption={getSelectedOption}
                getSelectedParticipant={getSelectedParticipant}
                createOrUpdateWorkerShiftLoader={createOrUpdateWorkerShiftLoader}
                handleChangeServiceCategory={handleChangeServiceCategory}
                watchFormData={watchFormData}
                calendarView={calendarView}
                setParticipantOptions={setParticipantOptionsSorted}
                participantOptions={participantOptions}
                register={register}
                setValue={setValue}
                selectedShift={selectedShift}
                checkRunningShiftStatus={checkRunningShiftStatus}
                checkDisabilityOfShiftStatus={checkDisabilityOfShiftStatus}
                handleChangeWorkerOrParticipant={handleChangeWorkerOrParticipant}
                isFetchRecordDetailLoader={isFetchRecordDetailLoader}
                control={control}
                watch={watch}
                setSelectedOptions={setSelectedOptions}
                selectedOptions={selectedOptions}
                clearErrors={clearErrors}
                errors={errors}
                userItem={userItem}
                setIsWorkerLoaded={setIsWorkerLoaded}
                isCallFromAddShift={isCallFromAddShift}
                setWorkerOptions={setWorkerOptionsSorted}
                setIsParticipantLoaded={setIsParticipantLoaded}
                isParticipantLoaded={isParticipantLoaded}
                selectedParticipant={selectedParticipant}
                isOpenShift={watchFormData?.open_shift}
                shiftDetailData={shiftDetailData}
                setShouldCallHandleChangeWorkerOrParticipant={
                  setShouldCallHandleChangeWorkerOrParticipant
                }
                serviceTypeSelectedOptions={serviceTypeSelectedOptions}
                prefillFromShiftRequest={prefillFromShiftRequest}
              />
            </AdvanceModeSectionBody>
          </AdvanceModeSection>
        </div>
      </div>

      {/* 2. Time & Location (with split segments) */}
      <div id={BASIC_SECTION_IDS.schedule} className='scroll-mt-3'>
        <BasicModeShiftWhen
          shiftDate={watchFormData?.shift_date ?? watchFormData?.date}
          startTime={watchFormData?.start_time}
          endTime={watchFormData?.end_time}
          register={register}
          setValue={setValue}
          getValues={getValues}
          watch={watch}
          clearErrors={clearErrors}
          trigger={trigger}
          errors={errors}
          selectedShift={selectedShift}
          checkDisabilityOfShiftStatus={checkDisabilityOfShiftStatus}
          isRecurringShiftReadOnly={disablDueToRecurringShift()}
          isShiftDateLocked={isShiftDateLocked}
          onShiftDateChange={onShiftDateChange}
          onAddSplitShift={handleAddSplitShift}
          splitSegments={
            <>
              {workerTimesPanel}
              {splitSegments}
            </>
          }
        />
      </div>

      {/* 3. Service — only when not split (split segments live under Time & Location) */}
      {!isShiftSplit && (
        <div id={BASIC_SECTION_IDS.service} className='scroll-mt-3'>
          <AdvanceModeSection>
            <AdvanceModeSectionHeader
              theme='emerald'
              icon={HiClipboardList}
              title='Service & billing'
              bordered={false}
              compact
            />
            <AdvanceModeSectionBody className='!pt-0' compact>
              <div className='grid min-w-0 grid-cols-1 gap-2 lg:grid-cols-2'>
                <div className='advance-mode-panel min-w-0'>
                  <AdvanceModeFieldLabel required>
                    Service category
                  </AdvanceModeFieldLabel>
                  <div
                    onMouseDown={(e) => {
                      const target = e.target;
                      if (
                        target.closest('[class*="css-"][class*="control"]') ||
                        target.closest(".select-basic-single") ||
                        target.closest(".roleSelector")
                      ) {
                        if (!target.closest('[class*="menu"]')) {
                          e.stopPropagation();
                        }
                      }
                    }}
                  >
                    <SelectOption
                      data={groupedServiceTypes}
                      isDisabled={checkRunningShiftStatus(selectedShift?.status)}
                      isRequired={true}
                      register={register}
                      setValue={setValue}
                      clearErrors={clearErrors}
                      name='invoice_service_type_id'
                      cssClass='z-20 select-text-xs'
                      selectedOption={selectedServiceCategory}
                      newDesign={true}
                      detachRegisterRef={true}
                      handleChange={(option) => {
                        const nextId = option?.value ?? null;
                        setValue("invoice_service_type_id", nextId, {
                          shouldValidate: showServiceCategoryError,
                          shouldDirty: true,
                        });
                        if (nextId != null && nextId !== "") {
                          clearErrors?.("invoice_service_type_id");
                        }
                        handleChangeServiceCategory?.(nextId);
                      }}
                      errorMessage={
                        showServiceCategoryError &&
                        (errors?.invoice_service_type_id?.type === "required" ||
                          errors?.invoice_service_type_id?.message)
                          ? errors?.invoice_service_type_id?.message ||
                            "This field is required!"
                          : null
                      }
                    />
                  </div>
                </div>
                <div className='advance-mode-panel flex min-w-0 items-center'>
                  <p className='text-xs font-medium text-slate-700'>
                    Ratio (Worker: {participantLabelPlural}){" "}
                    <span className='font-semibold tabular-nums text-slate-900'>
                      {workerCount}
                    </span>
                    {" : "}
                    <span className='font-semibold tabular-nums text-slate-900'>
                      {participantCount}
                    </span>
                  </p>
                </div>
              </div>
            </AdvanceModeSectionBody>
          </AdvanceModeSection>
        </div>
      )}

      {/* 4. Tasks & Notes — same footer section as advance */}
      <AdvanceModeSection id={BASIC_SECTION_IDS.tasksNotes}>
        <AdvanceModeSectionHeader title='Tasks & Notes' theme='amber' compact />
        <AdvanceModeSectionBody className='!pt-0' compact>
          <ShiftTasksNotesSection
            embedded
            hideHeader
            tasks={tasks}
            notes={notes}
            selectedShift={selectedShift}
            checkRunningShiftStatus={checkRunningShiftStatus}
            onTaskChange={handleTaskChange}
            onTaskMandatoryChange={handleTaskMandatoryChange}
            onAddTask={handleAddTask}
            onTaskDelete={handleTaskDelete}
            onNoteChange={handleNoteChange}
            isGroupShift={parseBooleanVal(watchFormData?.is_group_shift)}
            selectedParticipant={selectedParticipant}
          />

          {isShiftInstructionLoading ? (
            <div className='flex items-center justify-center py-3'>
              <Loader cssClass='mt-2' isScheduleScreen />
            </div>
          ) : (
            shiftInstructions?.length > 0 &&
            shiftInstructions?.map((instruction, index) => (
              <div
                key={index}
                className='mb-1.5 mt-2 rounded-lg border border-slate-200/80 bg-slate-50/80 p-2.5'
              >
                <label className='mb-1 block text-xs font-semibold text-slate-700'>
                  Shift Instructions for {capitalizeWords(instruction?.first_name)}{" "}
                  {capitalizeWords(instruction?.last_name)}
                </label>

                {instruction?.shift_instruction ||
                editableInstructionIndex === index ? (
                  <div>
                    <textarea
                      rows={2}
                      value={instruction?.shift_instruction || ""}
                      onChange={(e) => handleShiftInstructionChange(index, e)}
                      className='w-full rounded-lg border border-slate-200/90 bg-white p-2 text-xs text-slate-900 transition-all focus:border-transparent focus:outline-none focus:ring-2 focus:ring-indigo-500/40'
                      aria-label={`Shift instructions for ${instruction?.first_name} ${instruction?.last_name}`}
                    />
                    <button
                      type='button'
                      onClick={() => handleRemoveShiftInstruction(index)}
                      className='mt-1 text-xs font-semibold text-rose-600 hover:underline focus:outline-none'
                      aria-label={`Remove shift instructions for ${instruction?.first_name} ${instruction?.last_name}`}
                    >
                      Remove Shift Instructions
                    </button>
                  </div>
                ) : (
                  <button
                    type='button'
                    onClick={() => handleAddShiftInstruction(index)}
                    className='w-full rounded-lg border border-dashed border-indigo-200/90 bg-indigo-50/50 px-2 py-1.5 text-xs font-medium text-indigo-700 transition-all hover:border-indigo-300 hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500/40'
                    aria-label={`Add shift instructions for ${instruction?.first_name} ${instruction?.last_name}`}
                  >
                    + Add Shift Instructions (You can also setup default shift
                    instructions from the {participantLabel.toLowerCase()}&apos;s settings screen)
                  </button>
                )}
              </div>
            ))
          )}
        </AdvanceModeSectionBody>
      </AdvanceModeSection>
    </div>
  );
};

export default memo(BasicModeView);
